/**
 * Bind one collection to the cross-tab channel.
 *
 * ## Optimistic broadcast and deduplication
 *
 * Overlays ARE broadcast immediately so peers see changes before the server
 * responds. When the API call succeeds, the resulting `'mutation'` event is
 * suppressed for any key already communicated optimistically — the peer already
 * holds the correct value and a redundant write would race with other in-flight
 * updates. If the call fails, `'rollback'` is broadcast and the peer reverts.
 *
 * Deletes within a `'mutation'` event are always propagated regardless of
 * optimistic state, because they clean up phantom records created by an
 * optimistic insert when the server assigns a different real id.
 *
 * ## Why this cannot loop
 *
 * Three independent guards, each covering a case the others miss:
 *
 * 1. **Sender id** — a tab drops its own envelopes (`TabSync.receive`).
 * 2. **Provenance** — only mutation-originated writes are broadcast. A push
 *    tagged `'tab-sync'` (what this module applies) or `'ws'` never is. The
 *    socket case matters most: every tab receives the socket event
 *    independently, so re-broadcasting it would produce N×(N−1) redundant
 *    messages per event.
 * 3. **Re-entrancy counter** — belt to the provenance braces, covering any
 *    write an `apply` implementation makes through a path that forgot the tag.
 *
 * `purge` and `clear` are deliberately NOT propagated: both are local memory
 * management, not statements about server state. Broadcasting a route-unmount
 * purge would wipe a peer that is still showing that very data.
 */ export function attachTabSync(options) {
    const { collection, sync, apply, acceptUnknown = false, broadcastFetches = false } = options;
    const collectionId = collection.id;
    let applying = 0;
    /**
   * Outbound coalescing.
   *
   * One user action fans out into many synchronous writes — a cross-phase drop
   * emitted ~35 separate project upserts (optimistic token patches, phase
   * reorders, the settle refetch), each a full Project structured-cloned to
   * every peer. They all describe the SAME entity, so only the last one
   * matters. Buffer by key and post once per tick; the peer applies one
   * message instead of 35.
   */ const pendingUpserts = new Map();
    const pendingDeletes = new Set();
    const pendingCreated = new Set();
    let outboundScheduled = false;
    /**
   * Keys broadcast via `'optimistic'` that have not yet been resolved by a
   * `'mutation'` commit or `'rollback'`. Used to suppress the redundant commit
   * broadcast when the API call succeeds — the peer already holds the correct
   * speculative value.
   */ const optimisticallyBroadcast = new Set();
    const flushOutbound = ()=>{
        outboundScheduled = false;
        if (pendingUpserts.size > 0) {
            const entities = [
                ...pendingUpserts.values()
            ];
            const created = [
                ...pendingCreated
            ].filter((id)=>pendingUpserts.has(id));
            pendingUpserts.clear();
            pendingCreated.clear();
            sync.post({
                t: 'upsert',
                c: collectionId,
                entities: entities,
                created: created.length > 0 ? created : undefined
            });
        }
        if (pendingDeletes.size > 0) {
            const ids = [
                ...pendingDeletes
            ];
            pendingDeletes.clear();
            sync.post({
                t: 'delete',
                c: collectionId,
                ids
            });
        }
    };
    const scheduleOutbound = ()=>{
        if (outboundScheduled) {
            return;
        }
        outboundScheduled = true;
        setTimeout(flushOutbound, 0);
    };
    const push = (entities, opts)=>{
        collection.push(entities, {
            mode: 'merge',
            source: 'tab-sync',
            ...opts
        });
    };
    const unsubscribeWrites = collection.subscribeToWrites((event)=>{
        var // `flushOutbound` already filters `pendingCreated` to only keys present
        // in `pendingUpserts`, so skipped upserts never carry a stale created flag.
        _event_created;
        if (applying > 0) {
            return;
        }
        if (!sync.isEnabled) {
            return;
        }
        let upserts;
        let deletes;
        if (event.origin === 'optimistic') {
            // Broadcast the speculative value immediately and remember which keys
            // were sent so we can suppress the redundant commit on API success.
            upserts = event.upserts;
            deletes = event.deletes;
            upserts.forEach((entity)=>optimisticallyBroadcast.add(collection.keyOf(entity)));
        } else if (event.origin === 'rollback') {
            // Mutation failed — peers must revert. Remove keys from the tracking
            // set so a future insert/update for the same id is not suppressed.
            upserts = event.upserts;
            deletes = event.deletes;
            upserts.forEach((entity)=>optimisticallyBroadcast.delete(collection.keyOf(entity)));
            deletes.forEach((id)=>optimisticallyBroadcast.delete(id));
        } else if (event.origin === 'mutation') {
            // API call succeeded. Skip upserts the peer already holds from the
            // earlier optimistic broadcast — sending them again would race with
            // concurrent in-flight updates on the peer.
            //
            // `origin` says HOW the store was written; `source` says WHY. They are
            // independent: `push(x, { source: 'mutation' })` — the shape used by
            // every "call the API, then mirror the response" wrapper — has
            // `origin: 'push'`, NOT `origin: 'mutation'`.
            upserts = event.upserts.filter((entity)=>{
                const key = collection.keyOf(entity);
                if (optimisticallyBroadcast.has(key)) {
                    optimisticallyBroadcast.delete(key);
                    return false;
                }
                return true;
            });
            // Deletes are always propagated: they clean up phantom records that
            // peers created from an optimistic insert when the server assigned a
            // different real id on commit.
            deletes = event.deletes;
            deletes.forEach((id)=>optimisticallyBroadcast.delete(id));
        } else {
            // 'push', 'external-delete' — same logic as before.
            // `source` carries the caller's provenance for push events.
            const isLocalMutation = event.source === 'mutation';
            const propagate = // Imperative write mirrored in via push, or a client-computed cascade.
            event.origin === 'push' && isLocalMutation || // `externalDelete` defaults to `source: 'ws'`, which every tab
            // already receives on its own. Only removals a caller explicitly
            // attributed to a local mutation are worth sending.
            event.origin === 'external-delete' && isLocalMutation || event.origin === 'push' && broadcastFetches && event.source === 'fetch';
            if (!propagate) {
                return;
            }
            upserts = event.upserts;
            deletes = event.deletes;
        }
        upserts.forEach((entity)=>{
            const key = collection.keyOf(entity);
            // Last write for this key wins — a later value supersedes an earlier
            // one in the same tick, and re-queuing also cancels a pending delete.
            pendingUpserts.set(key, entity);
            pendingDeletes.delete(key);
        });
        (_event_created = event.created) === null || _event_created === void 0 ? void 0 : _event_created.forEach((id)=>pendingCreated.add(id));
        deletes.forEach((id)=>{
            pendingDeletes.add(id);
            pendingUpserts.delete(id);
            pendingCreated.delete(id);
        });
        if (pendingUpserts.size > 0 || pendingDeletes.size > 0) {
            scheduleOutbound();
        }
    });
    const unsubscribeMessages = sync.subscribe((msgs)=>{
        applying += 1;
        try {
            msgs.forEach((msg)=>{
                if (msg.t === 'upsert' && msg.c === collectionId) {
                    var _msg_created;
                    const incoming = msg.entities;
                    // A newly-created record is accepted even though this tab has never
                    // seen it — that is the whole point of hearing about a creation. An
                    // EDIT to an unknown id stays filtered out.
                    const createdKeys = new Set((_msg_created = msg.created) !== null && _msg_created !== void 0 ? _msg_created : []);
                    const accepted = acceptUnknown ? incoming : incoming.filter((entity)=>{
                        const key = collection.keyOf(entity);
                        return createdKeys.has(key) || collection.has(key);
                    });
                    if (accepted.length === 0) {
                        return;
                    }
                    if (apply) {
                        apply(accepted, (entities)=>push(entities));
                    } else {
                        push(accepted);
                    }
                    return;
                }
                if (msg.t === 'delete' && msg.c === collectionId) {
                    // `externalDelete`, never `delete()` — the latter would re-run the
                    // persist handler and fire a DELETE request from every open tab.
                    msg.ids.forEach((id)=>collection.externalDelete(id));
                    return;
                }
                if (msg.t === 'clear') {
                    collection.clear();
                }
            });
        } finally{
            applying -= 1;
        }
    });
    return ()=>{
        unsubscribeWrites();
        unsubscribeMessages();
        // Drop anything still buffered — the collection is detaching, and posting
        // after teardown would outlive the channel this adapter was bound to.
        pendingUpserts.clear();
        pendingDeletes.clear();
        pendingCreated.clear();
        optimisticallyBroadcast.clear();
    };
}
